import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Users, Activity, AlertCircle, CheckCircle2, BookOpen } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import AdminLayout from '../components/AdminLayout';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchAdminData();
  }, [navigate]);

  const fetchAdminData = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const headers = { Authorization: `Bearer ${token}` };

      const [statsRes, usersRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/stats`, { headers }),
        axios.get(`${API_URL}/api/admin/users?limit=10`, { headers })
      ]);

      setStats(statsRes.data);
      setUsers(usersRes.data.users);
    } catch (error) {
      console.error('Error fetching admin data:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminUser');
        navigate('/admin/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    navigate('/admin/login');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!stats) return null;

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Admin Header */}
        <div>
          <h2 className="text-3xl font-bold text-gray-900">Dashboard</h2>
          <p className="text-gray-600 mt-1">DiaWise Platform Overview</p>
        </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="shadow-md border-0">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Users</CardTitle>
            <Users className="w-5 h-5 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-gray-900">{stats.users.total}</div>
            <p className="text-sm text-gray-600 mt-1">
              Total on platform
            </p>
          </CardContent>
        </Card>

        <Card className="shadow-md border-0">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Blood Sugar</CardTitle>
            <Activity className="w-5 h-5 text-emerald-600" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-gray-900">{stats.glucose.total_logs.toLocaleString()}</div>
            <p className="text-sm text-gray-600 mt-1">
              Avg: {stats.glucose.average} mg/dL
            </p>
          </CardContent>
        </Card>

        <Card className="shadow-md border-0">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Content</CardTitle>
            <BookOpen className="w-5 h-5 text-orange-600" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-gray-900">{stats.content.products}</div>
            <p className="text-sm text-gray-600 mt-1">
              {stats.content.recipes} recipes
            </p>
          </CardContent>
        </Card>

        <Card className="shadow-md border-0">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Alerts</CardTitle>
            <AlertCircle className="w-5 h-5 text-red-600" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-red-600">{stats.glucose.high_readings + stats.glucose.low_readings}</div>
            <p className="text-sm text-gray-600 mt-1">
              Critical readings
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Platform Metrics */}
      <Card className="shadow-md border-0">
        <CardHeader>
          <CardTitle>Platform Metrics</CardTitle>
          <CardDescription>Activity Overview</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="p-4 bg-gradient-to-br from-gray-50 to-blue-50 rounded-lg border border-gray-200">
              <p className="text-sm text-gray-600 mb-1">Meals Logged</p>
              <p className="text-2xl font-bold text-gray-900">{stats.content.meals_logged.toLocaleString()}</p>
            </div>
            <div className="p-4 bg-gradient-to-br from-gray-50 to-green-50 rounded-lg border border-gray-200">
              <p className="text-sm text-gray-600 mb-1">Medications Tracked</p>
              <p className="text-2xl font-bold text-gray-900">{stats.content.medications.toLocaleString()}</p>
            </div>
            <div className="p-4 bg-gradient-to-br from-gray-50 to-purple-50 rounded-lg border border-gray-200">
              <p className="text-sm text-gray-600 mb-1">Shop Products</p>
              <p className="text-2xl font-bold text-gray-900">{stats.content.products}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs for different admin sections */}
      <Tabs defaultValue="users" className="space-y-6">
        <TabsList className="grid w-full max-w-md grid-cols-3">
          <TabsTrigger value="users">Users</TabsTrigger>
          <TabsTrigger value="alerts">Alerts</TabsTrigger>
          <TabsTrigger value="system">System</TabsTrigger>
        </TabsList>

        {/* Users Tab */}
        <TabsContent value="users">
          <Card className="shadow-md border-0">
            <CardHeader>
              <CardTitle>Recent Users</CardTitle>
              <CardDescription>Platform user list</CardDescription>
            </CardHeader>
            <CardContent>
              {users.length === 0 ? (
                <p className="text-center text-gray-500 py-8">No users found</p>
              ) : (
                <div className="space-y-3">
                  {users.map((user) => (
                    <div
                      key={user.id}
                      className="flex items-center justify-between p-4 bg-gradient-to-r from-gray-50 to-white rounded-lg border border-gray-200 hover:shadow-md transition-all"
                    >
                      <div className="flex items-center space-x-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-cyan-500 rounded-full flex items-center justify-center text-white font-semibold text-lg">
                          {user.name?.charAt(0) || 'U'}
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900">{user.name}</p>
                          <p className="text-sm text-gray-600">{user.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-6 text-sm">
                        <div className="text-center">
                          <p className="text-xs text-gray-600">Diabetes Type</p>
                          <p className="font-bold text-gray-900">{user.diabetes_type || 'N/A'}</p>
                        </div>
                        <div className="text-center">
                          <p className="text-xs text-gray-600">Target Blood Sugar</p>
                          <p className="font-bold text-gray-900">
                            {user.target_glucose_min}-{user.target_glucose_max}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Alerts Tab */}
        <TabsContent value="alerts">
          <Card className="shadow-md border-0">
            <CardHeader>
              <CardTitle>Alerts Système</CardTitle>
              <CardDescription>Critical notifications requiring attention</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {stats.glucose.high_readings > 0 && (
                  <div className="flex items-start space-x-3 p-4 rounded-lg border bg-red-50 border-red-200">
                    <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">
                        {stats.glucose.high_readings} high blood sugar readings detected
                      </p>
                      <p className="text-xs text-gray-600 mt-1">Requires medical follow-up</p>
                    </div>
                  </div>
                )}
                
                {stats.glucose.low_readings > 0 && (
                  <div className="flex items-start space-x-3 p-4 rounded-lg border bg-orange-50 border-orange-200">
                    <AlertCircle className="w-5 h-5 text-orange-600 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">
                        {stats.glucose.low_readings} low blood sugar readings detected
                      </p>
                      <p className="text-xs text-gray-600 mt-1">Risk of hypoglycemia</p>
                    </div>
                  </div>
                )}
                
                <div className="flex items-start space-x-3 p-4 rounded-lg border bg-blue-50 border-blue-200">
                  <CheckCircle2 className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">
                      System operational
                    </p>
                    <p className="text-xs text-gray-600 mt-1">All services running normally</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* System Tab */}
        <TabsContent value="system">
          <div className="grid md:grid-cols-2 gap-6">
            <Card className="shadow-md border-0">
              <CardHeader>
                <CardTitle>Database Statistics</CardTitle>
                <CardDescription>Current storage metrics</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between items-center py-2 border-b">
                  <span className="text-sm text-gray-600">Total Users</span>
                  <span className="font-semibold text-gray-900">{stats.users.total}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b">
                  <span className="text-sm text-gray-600">Lectures Blood Sugar</span>
                  <span className="font-semibold text-gray-900">{stats.glucose.total_logs.toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b">
                  <span className="text-sm text-gray-600">Meals Logged</span>
                  <span className="font-semibold text-gray-900">{stats.content.meals_logged.toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="text-sm text-gray-600">Recipes Available</span>
                  <span className="font-semibold text-gray-900">{stats.content.recipes}</span>
                </div>
              </CardContent>
            </Card>

            <Card className="shadow-md border-0">
              <CardHeader>
                <CardTitle>System Health</CardTitle>
                <CardDescription>Platform Status</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between items-center py-2 border-b">
                  <span className="text-sm text-gray-600">Shop Products</span>
                  <span className="font-semibold text-gray-900">{stats.content.products}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b">
                  <span className="text-sm text-gray-600">Medications Tracked</span>
                  <span className="font-semibold text-gray-900">{stats.content.medications}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b">
                  <span className="text-sm text-gray-600">Blood Sugar Moyenne</span>
                  <span className="font-semibold text-emerald-700">{stats.glucose.average} mg/dL</span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="text-sm text-gray-600">System Status</span>
                  <Badge className="bg-emerald-100 text-emerald-700">✓ Operational</Badge>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
